// import Vue from "vue";
import set from "lodash/set";
// import { resize } from "mathjs";
import { getLowerTriangle, expand, expandWithArray } from "@/helper/matrix";
import Vue from "vue";
import $axios from "@/axios-instance";
import store, { StoreError } from "../index";
import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import {
  AnnotationsState,
  AdjMat,
  Door,
  Node,
  Label,
  Edge,
  SvgDoor,
  Misc
} from "../types";

@Module({ dynamic: true, namespaced: true, name: "annotations", store })
class Annotations extends VuexModule implements AnnotationsState {
  name = "";
  filename = "";
  adjMat = [] as AdjMat;
  doors = [] as Door[][];
  nodes = [] as Node[];
  labels = [] as Label[];
  misc = [] as Misc[];
  tags = [] as string[];
  status = "" as string;
  editStartedAt = null as number | null;
  finishedAt = null as number | null;

  // Mutations
  @Mutation
  // TODO: make dedicated mutations !!
  set({ value, path }: { value: any; path: string }) {
    // if (path != "") {
    //   Vue.set(_.get(state, path), property, value);
    // } else {
    //   Vue.set(state, property, value);
    // }
    set(this, path, value);
  }

  @Mutation
  addNode({ newNode }: { newNode: Node }) {
    // expand matrices
    expand(this.adjMat, 1, 1, false);
    expandWithArray(this.doors, 1, 1);
    // insert new node
    this.nodes.push(newNode);
  }
  @Mutation
  removeNode({ i }: { i: number }) {
    // remove related edges
    // ------------------------------
    // remove row
    this.adjMat.splice(i, 1);
    this.doors.splice(i, 1);
    // remove column
    this.adjMat.forEach(o => {
      o.splice(i, 1);
    });
    this.doors.forEach(o => {
      o.splice(i, 1);
    });
    // remove node
    // ------------------------------
    this.nodes.splice(i, 1);
  }
  @Mutation
  addEdge({ i, j }: { i: number; j: number }) {
    const size = this.adjMat.length;
    if (i < 0 || j < 0 || i >= size || j >= size) {
      console.error("annotations/addEdge: out of bounds");
      return;
    }

    Vue.set(this.adjMat[j], i, true);
    Vue.set(this.adjMat[i], j, true);
  }
  @Mutation
  removeEdge({ i, j }: { i: number; j: number }) {
    const size = this.adjMat.length;
    if (i < 0 || j < 0 || i >= size || j >= size) {
      console.error("annotations/addEdge: out of bounds");
      return;
    }

    Vue.set(this.adjMat[i], j, false);
    Vue.set(this.adjMat[j], i, false);
  }
  @Mutation
  addLabel({ newLabel }: { newLabel: Label }) {
    this.labels.push(newLabel);
  }
  @Mutation
  removeLabel({ i }: { i: number }) {
    this.labels.splice(i, 1);
  }
  @Mutation
  addMisc(newMisc: Misc) {
    this.misc.push(newMisc);
  }
  @Mutation
  removeMisc(i: number) {
    this.misc.splice(i, 1);
  }
  @Mutation
  addDoor({
    i,
    j,
    r1,
    r2,
    name
  }: {
    i: number;
    j: number;
    r1: number;
    r2: number;
    name: string;
  }) {
    const size = this.doors.length;
    if (i < 0 || j < 0 || i >= size || j >= size) {
      console.error("annotations/addDoor: out of bounds");
      return;
    }
    // Push to the lower triangle
    if (i > j) {
      this.doors[i][j].push({ name, r1, r2 });
    } else {
      this.doors[j][i].push({ name, r1, r2 });
    }
  }
  @Mutation
  /**
   * @param {number} i - index of the first node in edge the door rests on
   * @param {number} j - index of the second node in edge the door rests on
   * @param {number} n - index of the door in the doors' array
   */
  removeDoorArray({ i, j }: { i: number; j: number }) {
    const size = this.doors.length;
    if (i < 0 || j < 0 || i >= size || j >= size) {
      console.error("annotations/addDoor: out of bounds");
      throw new StoreError("Node out of bounds.");
    }

    // Get item from the lower triangle
    if (i > j) {
      Vue.set(this.doors[i], j, []);
    } else {
      Vue.set(this.doors[j], i, []);
    }
  }
  @Mutation
  removeDoor({ i, j, n }: { i: number; j: number; n: number }) {
    const size = this.doors.length;
    if (i < 0 || j < 0 || i >= size || j >= size) {
      console.error("annotations/addDoor: out of bounds");
      throw new StoreError("Node out of bounds.");
    }

    let door = [];
    // Get item from the lower triangle
    if (i > j) {
      door = this.doors[i][j];
    } else {
      door = this.doors[i][j];
    }
    if (n >= door.length) {
      console.error("annotations/addDoor: out of bounds");
      throw new StoreError("Door out of bounds.");
    }
    door.splice(n, 1);
  }
  @Mutation
  setAnnotation(newValue: AnnotationsState) {
    Object.assign(this, newValue);
  }
  @Mutation
  setTags(val: string[]) {
    this.tags = val;
  }
  @Mutation
  addTag(val: string) {
    this.tags.push(val);
  }
  @Mutation
  removeTag(val: string) {
    const i = this.tags.indexOf(val);
    if (i > -1) {
      this.tags.splice(i, 1);
    }
  }
  @Mutation
  setEditStartedAt(val: number) {
    this.editStartedAt = val;
  }
  @Mutation
  setFinishedAt(val: number) {
    this.finishedAt = val;
  }

  @Mutation
  setState(newState: string) {
    this.status = newState;
  }

  get getNodes() {
    return this.nodes;
  }
  get getLabels() {
    return this.labels;
  }
  get getEdges() {
    const { rootState } = this.context;
    const ret: Edge[] = [];

    const lowerTriangle = getLowerTriangle(this.adjMat);
    const selectedEdges: { i: number; j: number }[] | null =
      rootState.ui.selectedEdges;

    lowerTriangle.forEach((row, i) => {
      row.forEach((item, j) => {
        let selected = false;
        if (selectedEdges) {
          selectedEdges.forEach(selectedEdge => {
            if (
              (selectedEdge.i === i + 1 && selectedEdge.j === j) ||
              (selectedEdge.i === j && selectedEdge.j === i + 1)
            ) {
              selected = true;
            }
          });
        }
        // draw edge between adjacent nodes
        if (item) {
          ret.push({
            selected,
            key: `edge:${i + 1}${j}`,
            nodes: [i + 1, j],
            pos: {
              // + 1 is needed for i since we are dealing with
              // the lower triangular matrix
              x1: this.nodes[i + 1].pos.cx,
              y1: this.nodes[i + 1].pos.cy,
              x2: this.nodes[j].pos.cx,
              y2: this.nodes[j].pos.cy
            }
          });
        }
      });
    });

    return ret;
  }
  get getAdjacentEdges() {
    return (nodeIndex: number): [Edge, number][] => {
      // return early if edge is empty
      if (this.getEdges.length === 0) return [];
      const edges: [Edge, number][] = [];
      this.getEdges.forEach((val, index) => {
        if (val.nodes[0] === nodeIndex || val.nodes[1] === nodeIndex) {
          edges.push([val, index]);
        }
      });
      return edges;
    };
  }
  get getDoors(): SvgDoor[] {
    const ret: SvgDoor[] = [];
    const lowerTriangle = getLowerTriangle(this.doors);

    lowerTriangle.forEach((row, i) => {
      row.forEach((items, j) => {
        if (items.length > 0) {
          // Find the edge the door is on
          const edge = this.getEdges.find(edge => {
            return edge.nodes[0] === i + 1 && edge.nodes[1] === j;
          });
          // If edge is not found, skip the current door
          // TODO: maybe delete it instead of skipping it everytime ?
          if (edge === undefined) {
            return;
          }
          const edgePos = edge.pos;
          const dx = edgePos.x2 - edgePos.x1;
          const dy = edgePos.y2 - edgePos.y1;

          items.forEach((item, index) => {
            ret.push({
              selected: false,
              key: `door:${i + 1}${j}${index}`,
              index,
              nodes: [i + 1, j],
              name: item.name ? item.name : "door",
              pos: {
                x1: edgePos.x1 + dx * item.r1,
                y1: edgePos.y1 + dy * item.r1,
                x2: edgePos.x1 + dx * item.r2,
                y2: edgePos.y1 + dy * item.r2
              }
            });
          });
        }
      });
    });

    return ret;
  }
  get getMisc() {
    return this.misc;
  }
  get neighbors() {
    return (index: number): number[] => {
      // !!! return empty on out-of-index
      if (index < 0 || index >= this.adjMat.length) {
        return [];
      }

      const neighbors: number[] = [];
      // i can check eithr column or row for finding neighbors,
      // but ill search through the row for no practical reason
      const row = this.adjMat[index];
      row.forEach((v, i) => {
        if (v) neighbors.push(i);
      });
      return neighbors;
    };
  }
  get getFilename() {
    return this.filename;
  }
  get getState() {
    return this.status;
  }
  get getTags() {
    return this.tags;
  }
  get getEditStartedAt() {
    return this.editStartedAt;
  }
  get getFinishedAt() {
    return this.finishedAt;
  }

  // actions
  @Action
  resetLocal() {
    this.setAnnotation({
      name: "",
      filename: "",
      adjMat: [],
      doors: [],
      nodes: [],
      labels: [],
      misc: [],
      tags: [],
      status: "",
      editStartedAt: null,
      finishedAt: null
    });
  }

  @Action({ rawError: true })
  async fetchAnnotation(targetId: string) {
    const { commit } = this.context;
    let res;

    // throw if targetId is empty
    if (!targetId) {
      commit("ui/setFetchFailed", true, { root: true });
      throw new StoreError("Floorplan not selected");
    }

    try {
      res = await $axios.get(`/floorplans/${targetId}`);
      if (res.data.success) {
        commit("ui/setFetchFailed", false, { root: true });
      } else {
        commit("ui/setFetchFailed", true, { root: true });
        throw new StoreError("Floorplan not found");
      }
    } catch (err) {
      commit("ui/setFetchFailed", true, { root: true });
      throw new StoreError("Server access failed");
    }

    const payload = res.data.payload;
    const newAnnotation = {
      ...payload.raw,
      status: payload.status,
      name: payload.name,
      tags: payload.tags || [],
      editStartedAt: payload.editStartedAt,
      finishedAt: payload.finishedAt
    };
    // if raw is empty, reset annotation
    if (!payload.raw) {
      Object.assign(newAnnotation, {
        adjMat: [],
        doors: [],
        nodes: [],
        labels: [],
        misc: [],
        tags: []
      });
    }
    // add selected prop to nodes
    // TODO: check if this typing works
    if (newAnnotation["nodes"]) {
      newAnnotation["nodes"] = newAnnotation["nodes"].map((node: Node) => {
        node["selected"] = false;
        return node;
      });
    }
    // use floorplan name as filename if filename is not specified
    newAnnotation["filename"] = payload.filename ? payload.filename : targetId;
    this.setAnnotation(newAnnotation);
  }
  @Action({ rawError: true })
  async postAnnotations(payload: {
    name: string;
    filename: string;
    status: string;
    adjMat: AdjMat;
    doors: Door[][];
    nodes: Node[];
    labels: Label[];
    misc: Misc[];
    tags: string[];
    editStartedAt: number | null;
    finishedAt: number | null;
  }) {
    // Throw if name is empty; leads to 404 error below
    if (!payload.name) {
      throw new StoreError("No floorplan is selected");
    }
    const req = {
      name: payload.name,
      filename: payload.filename,
      tags: payload.tags,
      status: payload.status,
      editStartedAt: payload.editStartedAt,
      finishedAt: payload.finishedAt,
      raw: {
        adjMat: payload.adjMat,
        doors: payload.doors,
        nodes: payload.nodes,
        labels: payload.labels,
        misc: payload.misc
      }
    };
    try {
      await $axios.put(`/floorplans/${payload.name}`, req);
    } catch (error) {
      throw new StoreError("Server access failed");
    }
  }
}

/* outputJson() {
    const walls = [];
    const bndbox = {};
    const junctions = [];
    const rooms = [];
    const doors = [];

    // about walls
    this.edges.forEach((edge, i) => {
      if (
        Math.abs(edge.pos.x1 - edge.pos.x2) <
        Math.abs(edge.pos.y1 - edge.pos.y2)
      ) {
        if (edge.pos.y1 < edge.pos.y2) {
          bndbox = {
            xmin: edge.pos.x1,
            xmax: edge.pos.x2,
            ymin: edge.pos.y1,
            ymax: edge.pos.y2
          };
        } else {
          bndbox = {
            xmin: edge.pos.x2,
            xmax: edge.pos.x1,
            ymin: edge.pos.y2,
            ymax: edge.pos.y1
          };
        }
      } else {
        if (edge.pos.x1 < edge.pos.x2) {
          bndbox = {
            xmin: edge.pos.x1,
            xmax: edge.pos.x2,
            ymin: edge.pos.y1,
            ymax: edge.pos.y2
          };
        } else {
          bndbox = {
            xmin: edge.pos.x2,
            xmax: edge.pos.x1,
            ymin: edge.pos.y2,
            ymax: edge.pos.y1
          };
        }
      }
      walls.push({
        index: i,
        bndbox: bndbox,
        junctions: edge.nodes
      });
    });

    // about junctions
    getters.nodes.forEach((node, i) => {
      junctions.push({
        index: i,
        x: node.pos.cx,
        y: node.pos.cy
      });
    });

    //TODO: about doors

    // about rooms
    getters.labels.forEach((label, i) => {
      rooms.push({
        type: label.name,
        index: i,
        x: label.pos.cx,
        y: label.pos.cy
      });
    });
    var json = {
      walls: walls,
      junctions: junctions,
      doors: doors,
      rooms: rooms
    };
    console.log(json);
  }, */

export default getModule(Annotations);
